// Clients, their contacts, client aliases and seniority (PRD rev. 1.19, P2M-a: CA1–CA4, CA7–CA8,
// MO7, MO9). Pure rules, shared by the API and the browser.
//
// A client alias is a 3Seven person's account in a client's tenant ("chris.carlson@unosquare.com").
// It belongs to a person and a CLIENT, never to a project (CA2): every project of that client shows
// the same record, so an edit on any card is an edit everywhere.

import type { ClientBilling } from "./billing-terms";
import type { Iso } from "./dates";

export const ALIAS_STATUSES = ["requested", "active", "disable-requested", "disabled"] as const;
export type AliasStatus = (typeof ALIAS_STATUSES)[number];

export const ALIAS_STATUS_LABEL: Readonly<Record<AliasStatus, string>> = {
  requested: "Requested",
  active: "Active",
  "disable-requested": "Disable requested",
  disabled: "Disabled",
};

export const CONTACT_PURPOSES = ["focal", "alias-admin", "billing", "other"] as const;
export type ContactPurpose = (typeof CONTACT_PURPOSES)[number];

export const CONTACT_PURPOSE_LABEL: Readonly<Record<ContactPurpose, string>> = {
  // The client's main point of contact for 3Seven (Chris, 2026-10-15).
  focal: "Focal",
  "alias-admin": "Alias admin",
  billing: "Billing",
  other: "Other",
};

export interface ClientContact {
  id: string;
  name: string;
  email: string;
  purpose: ContactPurpose;
  note?: string;
}

/**
 * A client record. Projects still carry the client's name (`Project.clientName`); the record is found
 * by that name, ignoring case and spacing, so no project had to be rewritten to adopt it.
 */
export interface Client {
  id: string;
  name: string;
  contacts: ClientContact[];
  notes?: string;
  /** Payment terms and the invoice table (CF1–CF2). Financial: never in the engine directory (SEC19). */
  billing?: ClientBilling;
}

/** One change of an alias's status, dated by the person who recorded it (CA1, CA8). */
export interface AliasEvent {
  status: AliasStatus;
  /** The day it happened, which can be earlier than the day it was recorded. */
  on: Iso;
  at: string;
  by: string;
  note?: string;
}

export interface ClientAlias {
  id: string;
  personId: string;
  clientId: string;
  /** Lower-cased. Unique across all aliases: an address belongs to one person (CA1). */
  address: string;
  status: AliasStatus;
  /** Oldest first; the last entry is the current status. */
  history: AliasEvent[];
  note?: string;
}

/** The id a client name maps to: "Logic 20/20" and "logic20/20 " are the same client. */
export function clientIdOf(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug ? `client-${slug}`.slice(0, 80) : "";
}

export const sameClient = (a: string, b: string) => clientIdOf(a) === clientIdOf(b);

const EMAIL = /^[^\s@<>()]+@[^\s@<>()]+\.[a-z]{2,}$/i;
export const isEmail = (s: string) => EMAIL.test(s.trim());

export function normalizeAddress(address: string): string {
  return address.trim().toLowerCase();
}

/** Problems with a contact list; empty when it can be saved. */
export function validateContacts(contacts: readonly ClientContact[]): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const c of contacts) {
    if (!c.id || ids.has(c.id)) errors.push("Every contact needs its own id.");
    ids.add(c.id);
    if (!c.name?.trim() || c.name.length > 80) errors.push("Every contact needs a name of up to 80 characters.");
    if (!isEmail(c.email ?? "")) errors.push(`"${c.email ?? ""}" isn't an email address.`);
    if (!(CONTACT_PURPOSES as readonly string[]).includes(c.purpose)) errors.push(`Contact ${c.name} has an unknown purpose.`);
    if (c.note !== undefined && c.note.length > 300) errors.push("A contact note is up to 300 characters.");
  }
  return errors;
}

/** Problems with a new or edited alias, given every other alias on file. */
export function validateAlias(alias: Pick<ClientAlias, "id" | "address" | "personId" | "clientId">, others: readonly ClientAlias[]): string[] {
  const errors: string[] = [];
  const address = normalizeAddress(alias.address ?? "");
  if (!isEmail(address)) errors.push(`"${alias.address ?? ""}" isn't an email address.`);
  if (/@3sevenconsulting\.com$/i.test(address)) errors.push("That's a 3Seven address, not a client alias.");
  if (!alias.personId) errors.push("Pick the person who holds the alias.");
  if (!alias.clientId) errors.push("Pick the client the alias belongs to.");
  const taken = others.find((o) => o.id !== alias.id && o.address === address);
  if (taken) errors.push(`${address} is already on file${taken.personId === alias.personId ? " for this person" : " for someone else"}.`);
  return errors;
}

/** The day an alias reached a status, from its history: the latest such entry. */
export function statusDate(alias: ClientAlias, status: AliasStatus): Iso | undefined {
  for (let i = alias.history.length - 1; i >= 0; i--) if (alias.history[i].status === status) return alias.history[i].on;
  return undefined;
}

/** When the alias's current status began. */
export const currentSince = (alias: ClientAlias): Iso | undefined => alias.history[alias.history.length - 1]?.on;

// --- CA4: the alias directory ------------------------------------------------------------------------

export interface DirectoryPerson {
  id: string;
  name: string;
  email?: string;
  status: "active" | "archived";
}

/**
 * Who an address belongs to on a given day, for Meeting Ops (CA4, MO1). A 3Seven account is its
 * person. A client alias resolves while it is requested, active or disable-requested, and a disabled
 * alias still resolves for meetings dated before it was disabled: the meeting happened while the
 * person held it.
 */
export function resolveAddress(
  address: string,
  on: Iso,
  people: readonly DirectoryPerson[],
  aliases: readonly ClientAlias[],
): { personId: string; via: "account" | "alias"; aliasId?: string } | null {
  const a = normalizeAddress(address);
  const person = people.find((p) => p.email && normalizeAddress(p.email) === a);
  if (person) return { personId: person.id, via: "account" };
  const alias = aliases.find((x) => x.address === a);
  if (!alias) return null;
  if (alias.status === "disabled") {
    const off = statusDate(alias, "disabled");
    if (off && on >= off) return null;
  }
  return { personId: alias.personId, via: "alias", aliasId: alias.id };
}

// --- CA7: what needs attention -----------------------------------------------------------------------

export interface AliasFlag {
  aliasId: string;
  kind: "archived-holder" | "disable-overdue";
  text: string;
}

/**
 * Aliases an admin should act on: a departed person still holding an alias that isn't on its way off,
 * and a disable request unconfirmed after `overdueDays`.
 */
export function aliasFlags(aliases: readonly ClientAlias[], people: readonly DirectoryPerson[], on: Iso, overdueDays = 7): AliasFlag[] {
  const byId = new Map(people.map((p) => [p.id, p]));
  const flags: AliasFlag[] = [];
  for (const a of aliases) {
    const person = byId.get(a.personId);
    if (person?.status === "archived" && (a.status === "active" || a.status === "requested")) {
      flags.push({ aliasId: a.id, kind: "archived-holder", text: `${person.name} is archived and still holds ${a.address}.` });
    }
    if (a.status === "disable-requested") {
      const since = currentSince(a);
      if (since && daysBetween(since, on) >= overdueDays) {
        flags.push({ aliasId: a.id, kind: "disable-overdue", text: `Disabling ${a.address} was requested ${daysBetween(since, on)} days ago and isn't confirmed.` });
      }
    }
  }
  return flags;
}

const daysBetween = (from: Iso, to: Iso) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

// --- MO9: seniority ----------------------------------------------------------------------------------

/**
 * The seniority order (MO9): the saved order first, then anyone active it doesn't name, in the
 * fallback order given (start date). Archived people drop out. The result is what decides who chases
 * a transcript when the project's owner wasn't in the meeting (MO3).
 */
export function seniorityOrder(saved: readonly string[], people: readonly DirectoryPerson[], fallback: readonly string[]): string[] {
  const active = new Set(people.filter((p) => p.status === "active").map((p) => p.id));
  const out: string[] = [];
  for (const id of [...saved, ...fallback, ...people.map((p) => p.id)]) {
    if (active.has(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

/** The most senior of `ids` by `order`; ids not in the order rank last, in their given order. */
export function mostSenior(ids: readonly string[], order: readonly string[]): string | undefined {
  const rank = (id: string) => {
    const i = order.indexOf(id);
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
  };
  return [...ids].sort((a, b) => rank(a) - rank(b))[0];
}
